// ONAV sheet variants (docs/schema-v2.md §5.0h): the official ONAV competition forms for STILL wines ("vini tranquilli", the sheet the
// app has always used) and SPARKLING wines ("vini spumanti e frizzanti", other criteria and maxima). Plain module with no imports so it
// can be tested with node (see onavVariants.test.mjs).
//
// STILL     visual /18  (clarity, hue, intensity 6)             olfactory /30 (6 + 8 + 8 + 8)              gustatory /44 (6 + 8 + 8 + 8 + 8 + 6)   final /8
// SPARKLING visual /30  (clarity, perlage size, perlage persistence, hue, intensity 6)   olfactory /28 (7 x 4)   gustatory /35 (7 x 5, no aftertaste)   final /7

export const VARIANTS = ['still', 'sparkling'];
export const normalizeVariant = (variant) => (variant === 'sparkling' ? 'sparkling' : 'still');   // sheets saved before the variant existed are still

// Wine types for which the sparkling form is suggested (never forced)
export const suggestVariant = (wineType) => (wineType === 'sparkling' || wineType === 'semi_sparkling' ? 'sparkling' : 'still');

const m = (key, max) => ({ key, max });

export const ONAV_LAYOUTS = {
  still: {
    visual: [m('visual_hue', 6), m('visual_intensity', 6), m('visual_clarity', 6)],          // form order of the app (hue, palette, intensity, clarity)
    olfactory: [m('olfactory_cleanness', 6), m('olfactory_intensity', 8), m('olfactory_finesse', 8), m('olfactory_complexity', 8)],
    gustatory: [m('gustatory_cleanness', 6), m('gustatory_intensity', 8), m('gustatory_body', 8), m('gustatory_harmony', 8),
      m('gustatory_persistence', 8), m('gustatory_aftertaste', 6)],
    final: [m('overall_impression', 8)],
    totals: { visual: 18, olfactory: 30, gustatory: 44, final: 8, total: 100 },
  },
  sparkling: {
    visual: [m('visual_clarity', 6), m('perlage_size', 6), m('perlage_persistence', 6), m('visual_hue', 6), m('visual_intensity', 6)],   // order of the official form
    olfactory: [m('olfactory_cleanness', 7), m('olfactory_intensity', 7), m('olfactory_finesse', 7), m('olfactory_complexity', 7)],
    gustatory: [m('gustatory_cleanness', 7), m('gustatory_intensity', 7), m('gustatory_body', 7), m('gustatory_harmony', 7),
      m('gustatory_persistence', 7)],
    final: [m('overall_impression', 7)],
    totals: { visual: 30, olfactory: 28, gustatory: 35, final: 7, total: 100 },
  },
};

export const layoutOf = (variant) => ONAV_LAYOUTS[normalizeVariant(variant)];

// Every scored field of a variant, with its maximum
export const markFields = (variant) => {
  const layout = layoutOf(variant);
  return [...layout.visual, ...layout.olfactory, ...layout.gustatory, ...layout.final];
};

// All the mark fields of both variants (to clamp when switching)
export const ALL_MARKS = [...new Set([...markFields('still'), ...markFields('sparkling')].map((x) => x.key))];

// ── Values PRINTED on each row of the official forms (marks can only take these values; the backend enforces the same table,
// `codes.ONAV_PRINTED_VALUES`, and onavVariants.test.mjs compares the two) ──
//   6-point rows: 6 5 4 3 2 1 0   8-point rows (still form): 8 7 6 5 4 2 0 (no 3, no 1)   7-point rows (sparkling): 7 6 5 4 3 2 0 (no 1)
//   rows that do not exist on the form (sparkling aftertaste): 0
export const printedValuesForMax = (max) => {
  if (max === 0) return [0];
  if (max === 8) return [8, 7, 6, 5, 4, 2, 0];
  if (max === 7) return [7, 6, 5, 4, 3, 2, 0];
  return Array.from({ length: max + 1 }, (_, i) => max - i);
};

export const PRINTED_VALUES = Object.fromEntries(VARIANTS.map((variant) => {
  const rows = markFields(variant);
  const table = Object.fromEntries(rows.map((row) => [row.key, printedValuesForMax(row.max)]));
  if (variant === 'sparkling') table.gustatory_aftertaste = [0];          // the sparkling form has no aftertaste row
  return [variant, table];
}));

// Printed values of a row of a variant, in the order printed on the form (descending); [] when the row does not exist on that form
export const printedValues = (variant, field) => (PRINTED_VALUES[normalizeVariant(variant)][field] || []).slice();

const isBlank = (v) => v === undefined || v === null || v === '';

// True when `value` is one of the values printed on that row (an integer; strings of digits are accepted)
export const isPrinted = (variant, field, value) => !isBlank(value) && Number.isInteger(Number(value)) && printedValues(variant, field).includes(Number(value));

// Nearest printed value NOT ABOVE `value` (3 on an 8-point row -> 2, 8 on a 7-point row -> 7, 1 on a 7-point row -> 0)
export function snapToPrinted(variant, field, value) {
  const printed = printedValues(variant, field);
  if (printed.length === 0) return 0;
  const v = Number(value);
  if (!Number.isFinite(v)) return 0;
  return printed.find((p) => p <= v) ?? 0;
}

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const sumOf = (rows, f) => rows.reduce((sum, row) => sum + Math.min(num(f[row.key]), row.max), 0);

// Totals of a form/sheet: only the fields of its variant count (a stale aftertaste or perlage mark is ignored)
export function calcMarksTotals(f, variant = f && f.variant) {
  const layout = layoutOf(variant);
  const visual = sumOf(layout.visual, f);
  const olfactory = sumOf(layout.olfactory, f);
  const gustatory = sumOf(layout.gustatory, f);
  const final = sumOf(layout.final, f);
  return { visual, olfactory, gustatory, final, total: visual + olfactory + gustatory + final };
}

// Form values after switching to `variant`: the common marks are kept, a value that is not printed on the new form is snapped to the
// nearest printed value not above it (so it never grows), the fields the new variant does not have are reset (aftertaste 0 on sparkling;
// perlage not selected on still, sent as null by the payload). Marks that are not selected yet (null / '' / undefined) stay as they are.
export function clampToVariant(f, variant) {
  const next = normalizeVariant(variant);
  const out = { ...f, variant: next };
  for (const row of markFields(next)) {
    if (isBlank(out[row.key])) continue;
    out[row.key] = snapToPrinted(next, row.key, out[row.key]);
  }
  if (next === 'sparkling') out.gustatory_aftertaste = 0;
  else { out.perlage_size = null; out.perlage_persistence = null; }
  return out;
}

// Rows of the variant whose stored value is NOT printed on the form (legacy sheets saved before the printed-values rule): never changed
// silently, the form highlights them and blocks the save until the user chooses a printed value
export function nonPrintedRows(f, variant = f && f.variant) {
  const v = normalizeVariant(variant);
  return markFields(v).filter((row) => !isBlank(f[row.key]) && !isPrinted(v, row.key, f[row.key])).map((row) => ({ key: row.key, value: f[row.key], printed: printedValues(v, row.key) }));
}
